import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Autocomplete, Box, Checkbox, CircularProgress, TextField, Typography } from '@mui/material';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import KosaStore from '../../../stores/kosa-store';
import { Kosa } from '../../../types/kosa';

dayjs.extend(utc);

export interface SnsFilterProps {
    nodeId: number;
    selectedSnsIds?: number[];
    onChange: (snsIds: number[]) => void;
}

function getStatusColor(lastTransaction: string): string {
    const diffHours = dayjs().diff(dayjs.utc(lastTransaction).local(), 'hour');
    if (diffHours < 24) return '#4caf50';
    if (diffHours < 48) return '#ff9800';
    return '#f44336';
}

const SnsFilter = observer(({ nodeId, selectedSnsIds, onChange }: SnsFilterProps) => {
    const [store] = useState(() => new KosaStore(nodeId));
    const [selectedItems, setSelectedItems] = useState<Kosa[]>([]);
    const initRef = useRef(selectedSnsIds ?? []);

    useEffect(() => {
        store
            .load()
            .then(() => {
                if (initRef.current.length > 0) {
                    setSelectedItems(store.items.filter((k) => initRef.current.includes(k.id)));
                }
            })
            .catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = (_: React.SyntheticEvent, newValue: Kosa[]) => {
        setSelectedItems(newValue);
        onChange(newValue.map((k) => k.id));
    };

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            options={store.items}
            value={selectedItems}
            onChange={handleChange}
            loading={store.loading}
            getOptionLabel={(option) => `Коса-${option.id}`}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            noOptionsText="Нет сенсоров"
            size="small"
            sx={{ mx: 1, mb: 1 }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={selectedItems.length === 0 ? 'Все' : undefined}
                    slotProps={{
                        input: {
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {store.loading && <CircularProgress size={14} />}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        },
                    }}
                />
            )}
            renderOption={(props, option, { selected }) => (
                <li {...props} key={option.id}>
                    <Checkbox size="small" checked={selected} disableRipple sx={{ p: 0, mr: 1 }} />
                    <DeviceThermostatIcon sx={{ fontSize: 14, mr: 0.5, flexShrink: 0, color: 'text.secondary' }} />
                    <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                        Коса-{option.id}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, mx: 1 }}>
                        {option.sensorCount} дат
                    </Typography>
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: getStatusColor(option.lastTransaction),
                            flexShrink: 0,
                        }}
                    />
                </li>
            )}
        />
    );
});

export default SnsFilter;
